import { memo, useEffect, useMemo, useState } from "react";
import { Button, Form, Icon, Input, Modal } from "semantic-ui-react";

// Components
import CardPatientQueueUX from "react-lib/apps/MEH/CardPatientQueueUX";
import CardMeVitalSign from "react-lib/apps/MEH/CardMeVitalSign";
import CardMeDocument from "react-lib/apps/MEH/CardMeDocument";
import CardPrintList from "react-lib/apps/common/CardPrintList";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import ModAuthen from "react-lib/apps/common/ModAuthen";
import ModNewRegister, { NewRegisterFormData } from "react-lib/apps/MEH/ModNewRegister";
import ModMeBillingCustomer from "react-lib/apps/MEH/ModMeBillingCustomer";
import ModMeLabVideoQueue from "react-lib/apps/MEH/ModMeLabVideoQueue";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import {
  BTN_ACTS,
  PatientQueueDataType,
  PatientQueueType,
  PickedState,
} from "react-lib/apps/MEH/sequence/PatientQueue";

// Types
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { CreateCustomerParams, SEQUENCE_NAME } from "./types/MeBillingType";

// Serializers
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";

type CardPatientQueueProps = {
  //
  runSequence: any;
  setProp: any;
  onEvent: any;
  django: any;
  errorMessage?: Record<string, any>;
  // data
  selectedEncounter?: Record<string, any>;
  selectedPatient?: Record<string, any>;
  PatientQueueSequence?: PatientQueueType;

  // options
  doctorList: DoctorSerializer[];

  // print list
  printListController?: Record<string, any>;

  // scanner
  scannedDocumentController?: any;
  patientData?: Record<string, any>;

  // Common
  languageUX: "en" | "th";
  selectedDivision: Record<string, any>;
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus">;

export const ICONS: Record<string, string> = {
  triageLevelGreen: "static/images/mePatientQueue/triageLevelGreen.png",
  triageLevelPink: "static/images/mePatientQueue/triageLevelPink.png",
  editDataUser: "static/images/mePatientQueue/editDataUser.png",
  iconListMenu: "static/images/mePatientQueue/iconListMenu.png",
  iconStatusScreened: "static/images/mePatientQueue/iconStatusScreened.png",
};

const CardPatientQueue = (props: CardPatientQueueProps) => {
  const [openModal, setOpenModal] = useState<string>("");
  const [openCustomerMod, setOpenCustomerMod] = useState<boolean>(false);
  const [openVideoQueue, setOpenVideoQueue] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    props.runSequence({
      sequence: "PatientQueue",
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  const encounterStatusOptions = useMemo(() => {
    const options = (props.masterOptions?.EncounterStatus || []).map((item) => ({
      ...item,
      text: item.text.replace(/แพทย์/g, "ผู้ให้บริการ"),
    }));

    return [{ key: "ALL", value: "ALL", text: "ทั้งหมด" }, ...options];
  }, [props.masterOptions?.EncounterStatus]);

  const handleSelectedTriageLevel = (item: PatientQueueDataType) => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "GET_TRIAGERLATEST",
      data: item,
      callback: (type: string) => {
        setOpenModal(type);
      },
    });
  };

  const handleSelectedPatient = (item: PatientQueueDataType, type: string) => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "SELECTED_PATIENT",
      data: item,
      type: type,
      callback: (type: string) => {
        setOpenModal(type);
      },
    });
  };

  const handleSelectedInitialMenu = (item: PatientQueueDataType, initialMenu: string) => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "SELECTED_INITIAL_MENU",
      data: item,
      initialMenu,
    });
  };

  const handlePrintLMR = (data: any) => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "PRINT_LMR",
      data,
    });
  };

  const handleSelectCustomer = (patientID: string | number) => {
    if (!patientID) {
      console.warn("[Patient Queue] PatientID not found!!");
      return;
    }

    props.runSequence({
      sequence: "MeBilling",
      action: "GET_PATIENT_DETAIL",
      patientID,
      onSuccess: setOpenCustomerMod(true),
    });
  };

  const handleUpdateStatusEncounter = (data: PatientQueueDataType) => {
    if (data.status === "SCREENED") {
      props.runSequence({
        sequence: "PatientQueue",
        action: "PATCH_ENCOUNTER",
        status: "OPD_QUEUE_EXAM",
        encounterId: data.pk,
      });
    } else if (data.status === "IN_QUEUE") {
      props.runSequence({
        sequence: "PatientQueue",
        action: "PATCH_ENCOUNTER",
        status: "OPD_CHECK_IN",
        encounterId: data.pk,
        isDischarge: true,
      });
    } else if (data.status === "CHECKED_OUT") {
      props.runSequence({
        sequence: "PatientQueue",
        action: "PATCH_ENCOUNTER",
        status: "OPD_CANCEL_CHECK_OUT",
        encounterId: data.pk,
        isCancelDischarge: true,
      });
    }
  };

  const handleCallQueue = (item: PatientQueueDataType) => {
    const centralLabOrders = (item?.doctor_orders || [])
      .filter((order: any) => order?.specific_type === "centrallaborder")
      .map((order: any) => order?.id);

    props.onEvent({
      message: "CallPhlebotomyQueue",
      params: {
        encounterId: item.id,
        labOrderId: centralLabOrders,
        patientId: item.patient,
        queueType: 1,
        vn: item.number,
        isCalled: item.is_called,
      },
    });
  };

  const patientQueueData = useMemo(() => {
    return (props.PatientQueueSequence?.patientQueueData || [])
      .sort((a, b) => (a.created_utc < b.created_utc ? -1 : 1))
      .map((item: PatientQueueDataType) => {
        const divisionHN = (item?.hn || "").replace(`${item?.division_code || ""}_`, "");

        return {
          ...item,
          hn: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ...(item.is_called
                  ? { color: "#21BA45" }
                  : item.is_appointment
                    ? { color: "#F382A0" }
                    : { color: "#2688D1" }),
              }}
            >
              <div>{divisionHN}</div>
              <div style={{ margin: "0px 15px 0px 5px" }}>({item.number})</div>

              <div
                style={{
                  border: "1px solid var(--primary-theme-color)",
                  borderRadius: "18%",
                  width: "21px",
                  height: "21px",
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleCallQueue(item)}
              >
                <Icon
                  className="bell icon"
                  style={{ color: "var(--primary-theme-color)", margin: "0px" }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginLeft: "5px",
                }}
                onClick={() => handleSelectedInitialMenu(item, "ลงทะเบียนรับบริการ")}
              >
                <img src={ICONS.iconListMenu} style={{ width: "21px", height: "21px" }} />
              </div>
            </div>
          ),
          patientName: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>{item.patient_name}</div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectCustomer(item?.patient_id || "")}
              >
                <img src={ICONS.editDataUser} style={{ width: "18px" }} />
              </div>
            </div>
          ),
          queueDate: (
            <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
              <div>{`${(item?.created || "").replace("[", "(").replace("]", ")")}`}</div>
              <div
                style={{
                  minWidth: "21px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {item?.is_appointment && (
                  <Icon
                    className="calendar icon"
                    style={{ color: "var(--primary-theme-color)", margin: "0px" }}
                  />
                )}
              </div>
            </div>
          ),
          appointmentDate: (
            <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
              <div>{`${(item?.appoint_time || "").replace("[", "(").replace("]", ")")}`}</div>
            </div>
          ),
          triageLevel: (
            <div
              style={{ display: "flex", justifyContent: "center", cursor: "pointer" }}
              onClick={() => handleSelectedTriageLevel(item)}
            >
              {item.triage_level === "" ? (
                <img src={ICONS.triageLevelPink} style={{ width: "35px" }} />
              ) : (
                <img src={ICONS.triageLevelGreen} style={{ width: "35px" }} />
              )}
            </div>
          ),
          printReport: (
            <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
              <Button
                onClick={() => handleSelectedPatient(item, "printForm")}
                style={{ background: "#2688D1", color: "white" }}
                size="small"
                icon="print"
              >
                {"Print Form"}
              </Button>
              <Button
                onClick={() => handleSelectedPatient(item, "document")}
                style={{ background: "#BF7877", color: "white" }}
                size="small"
                icon="upload"
              >
                {"Upload & View Document"}
              </Button>
            </div>
          ),
          printLMR: (
            <div
              style={{ display: "flex", justifyContent: "center", cursor: "pointer" }}
              onClick={() => handlePrintLMR(item)}
            >
              <Icon name="print" color="green" size="large"></Icon>
            </div>
          ),
          status: (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {item.status === "SCREENED" && (
                <div
                  style={{ cursor: "pointer", marginRight: "10px" }}
                  onClick={() => handleUpdateStatusEncounter(item)}
                >
                  <img src={ICONS.iconStatusScreened} style={{ width: "35px" }} />
                </div>
              )}

              <div>{encounterStatusOptions?.find((data) => data.value === item.status)?.text}</div>
            </div>
          ),
          discharge: (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                onClick={() => handleUpdateStatusEncounter(item)}
                style={{ background: "#0147A3", color: "white" }}
                size="small"
                disabled={["WAIT_TRIAGE", "ARRIVING", "SCREENED"].includes(item.status || "")}
              >
                {item.status === "CHECKED_OUT" ? "ยกเลิก Discharge" : "Discharge"}
              </Button>
            </div>
          ),
          cancel: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "15px",
              }}
            >
              <div
                style={{
                  border: "1px solid #FBC424",
                  borderRadius: "18%",
                  width: "21px",
                  height: "21px",
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleSelectedPatient(item, "editEncounter")}
              >
                <Icon className="edit icon" style={{ color: "#FBC424", margin: "0px" }} />
              </div>

              <Button
                color="red"
                size="small"
                onClick={() => {
                  setNote("");
                  handleSelectedPatient(item, "cancelEncounter");
                }}
              >
                ยกเลิก
              </Button>
            </div>
          ),
        };
      });
  }, [props.PatientQueueSequence?.patientQueueData, encounterStatusOptions]);

  const handleChangeFromDate = (date: any) => {
    props.setProp("PatientQueueSequence.patientQueueFilter.from_date", date);
  };

  const handleChangeToDate = (date: any) => {
    props.setProp("PatientQueueSequence.patientQueueFilter.to_date", date);
  };

  const handleChangeData = (_event: any, data: any) => {
    const { name, value, checked } = data;

    props.setProp(
      `PatientQueueSequence.patientQueueFilter.${name}`,
      checked !== undefined ? checked : value,
    );
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "SEARCH",
    });
  };

  const handlePrintPatientQueue = () => {
    props.runSequence({
      sequence: "PatientQueue",
      action: "PRINT_PATIENT_QUEUE",
    });
  };

  const handleCloseCustomerMod = () => {
    props.setProp("selectedPatient", null);
    setOpenCustomerMod(false);
  };

  const handleSaveCustomer = (data: CreateCustomerParams) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "UPDATE_CUSTOMER",
      customerId: props.selectedPatient?.id,
      customerData: data,
      onSuccess: handleCloseCustomerMod,
    });
  };

  const handleUpdateEncounter = (data: NewRegisterFormData) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "UPDATE_VISIT",
      visitData: {
        doctor: data.provider_id,
      },
      onSuccess: () => {
        setOpenModal("");
      },
    });
  };

  const handleDeleteEncounter = ({ username, password }: any = {}) => {
    props.runSequence({
      sequence: "MeBilling",
      action: "DELETE_VISIT",
      username: username,
      password: password,
      note: note,
      onSuccess: () => {
        setOpenModal("");
      },
    });
  };

  return (
    <>
      <SnackMessage
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
        error={props.errorMessage?.[SEQUENCE_NAME]}
      />

      <CardPatientQueueUX
        patientQueueFilter={props.PatientQueueSequence?.patientQueueFilter}
        limit={props.PatientQueueSequence?.limit}
        currentPage={props.PatientQueueSequence?.currentPage}
        totalPage={props.PatientQueueSequence?.totalPage}
        onChangeFromDate={handleChangeFromDate}
        onChangeToDate={handleChangeToDate}
        onChangeData={handleChangeData}
        handlePageChange={(page: number) => {
          props.runSequence({
            sequence: "PatientQueue",
            action: "SEARCH",
            currentPage: page,
          });
        }}
        handlePageSizeChange={(limit: number) => {
          props.runSequence({
            sequence: "PatientQueue",
            action: "SEARCH",
            currentPage: 0,
            limit: limit,
          });
        }}
        encounterStatusOptions={encounterStatusOptions}
        patientQueueData={patientQueueData}
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={BTN_ACTS.SEARCH}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            // config
            style={{ background: "#2688D1", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหา"}
          />
        }
        buttonPrint={
          <div style={{ display: "flex", gap: "10px" }}>
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handlePrintPatientQueue}
              // data
              paramKey={BTN_ACTS.PRINT_PATIENT_QUEUE}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_PATIENT_QUEUE]}
              // config
              style={{ background: "#8649E9", color: "white" }}
              fluid={true}
              size="small"
              title={"พิมพ์รายการผู้รับบริการ"}
            />

            <Button
              size="small"
              style={{ background: "#2688D1", color: "white", minWidth: "max-content" }}
              onClick={() => setOpenVideoQueue(true)}
            >
              สถานะ Video
            </Button>
          </div>
        }
      />

      <ModMeLabVideoQueue
        open={openVideoQueue}
        runSequence={props.runSequence}
        onClose={() => setOpenVideoQueue(false)}
      />

      <ModMeBillingCustomer
        open={openCustomerMod}
        onClose={handleCloseCustomerMod}
        onSave={handleSaveCustomer}
        setProp={props.setProp}
        initialData={props.selectedPatient}
        masterData={props.masterData}
        masterOptions={props.masterOptions}
        buttonLoadCheck={props.buttonLoadCheck}
        isPatientQueue={true}
        languageUX={props.languageUX}
      />

      <Modal size="small" open={openModal === "vitalSign"} onClose={() => setOpenModal("")}>
        <CardMeVitalSign
          runSequence={props.runSequence}
          setProp={props.setProp}
          onEvent={props.onEvent}
          selectedDivision={props.selectedDivision}
          buttonLoadCheck={props.buttonLoadCheck}
          selectedPatient={props.selectedPatient}
          selectedEncounter={props.selectedEncounter}
          masterData={props.masterData}
          masterOptions={props.masterOptions}
          PatientQueueSequence={props.PatientQueueSequence}
          onClose={(type?: string) => setOpenModal(type || "")}
          languageUX={props.languageUX}
        />
      </Modal>

      <Modal size="large" open={openModal === "printForm"} onClose={() => setOpenModal("")}>
        <CardPrintList
          printer={props.django?.user?.full_name}
          patientInfo={props.selectedPatient}
          controller={props.printListController}
          patientData={props.selectedPatient}
          patientId={props.selectedEncounter?.patient_id}
          encounterId={props.selectedEncounter?.id}
          encounterNumber={props.selectedEncounter?.number}
          masterData={props.masterData}
          masterOptions={props.masterOptions}
          selectedPatient={props.selectedPatient}
          selectedDivision={props.selectedDivision}
          onClose={() => setOpenModal("")}
          showPreOrder={true}
          defaultService={false}
          defaultEncounter={true}
          languageUX={props.languageUX}
        />
      </Modal>

      <Modal size="large" open={openModal === "document"} onClose={() => setOpenModal("")}>
        <CardMeDocument
          runSequence={props.runSequence}
          setProp={props.setProp}
          onEvent={props.onEvent}
          django={props.django}
          selectedPatient={props.selectedPatient}
          selectedEncounter={props.selectedEncounter}
          selectedDivision={props.selectedDivision}
          // scanner
          scannedDocumentController={props.scannedDocumentController}
          patientData={props.patientData}
          onClose={(type?: string) => setOpenModal(type || "")}
          languageUX={props.languageUX}
        />
      </Modal>

      <ModNewRegister
        open={openModal === "editEncounter"}
        setProp={props.setProp}
        // callbacks
        onClose={() => setOpenModal("")}
        onSave={(data) => handleUpdateEncounter(data)}
        // options
        doctorList={props.doctorList}
        // common
        isUpdateEncounter={true}
        selectedPatient={props.selectedPatient}
        selectedEncounter={props.selectedEncounter}
        selectedDivision={props.selectedDivision}
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />

      <ModAuthen
        titleName={"ระบุเหตุผลยกเลิก Encounter"}
        backgroundColor={"var(--primary-theme-color)"}
        open={openModal === "cancelEncounter"}
        onCloseWithDimmerClick={() => setOpenModal("")}
        onApprove={handleDeleteEncounter}
        onDeny={() => setOpenModal("")}
      >
        <Form.Field label={"เหตุผลการยกเลิก"} style={{ marginBottom: "0.5rem" }} />
        <Form.Field>
          <Input fluid={true} value={note} onChange={(e) => setNote(e.target.value)} />
        </Form.Field>
      </ModAuthen>
    </>
  );
};

export default memo(CardPatientQueue);
